import React, { memo } from 'react';
import { useLeadDeliveryFormContext } from '@/context/leadDelivery';
import { Controller, Control } from 'react-hook-form';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import TimeInput from './TimeInput';
import type { LeadDeliverySettings } from '@/context/leadDelivery';
import { START_OF_DAY, END_OF_DAY_UI } from '@/constants/time';

// Days of the week with their display labels
const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

// Individual day row component
const DayRow = memo(({ day, control, isOpen, defaultFrom, defaultTo, allDay }: {
  day: { key: string; label: string };
  control: Control<LeadDeliverySettings>;
  isOpen: boolean;
  defaultFrom: string;
  defaultTo: string;
  allDay: boolean;
}) => {
  const { setValue } = useLeadDeliveryFormContext();
  return (
    <tr key={day.key}>
      <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
        {day.label}
      </td>
      <td className="px-3 py-4 text-sm text-gray-500">
        <div className="flex items-center gap-2">
          <Controller
            name={`businessHours.${day.key}.open`}
            control={control}
            defaultValue={isOpen}
            render={({ field }) => (
              <Switch
                checked={field.value}
                onCheckedChange={(checked) => {
                  field.onChange(checked);
                  if (!checked) {
                    setValue(`businessHours.${day.key}.allDay`, false, { shouldDirty: true });
                    setValue(`businessHours.${day.key}.from`, '', { shouldDirty: true });
                    setValue(`businessHours.${day.key}.to`, '', { shouldDirty: true });
                  }
                }}
                className="data-[state=checked]:bg-primary"
                id={`day-toggle-${day.key}`}
              />
            )}
          />
          <span>{isOpen ? 'Open' : 'Closed'}</span>
        </div>
      </td>
      <td className="px-3 py-4 text-sm text-gray-500">
        <div className="flex items-center gap-2">
          <Controller
            name={`businessHours.${day.key}.from`}
            control={control}
            defaultValue={defaultFrom}
            render={({ field }) => (
              <TimeInput
                value={field.value}
                onChange={field.onChange}
                disabled={!isOpen || allDay}
                timeFormat="AM"
              />
            )}
          />
          <span>to</span>
          <Controller
            name={`businessHours.${day.key}.to`}
            control={control}
            defaultValue={defaultTo}
            render={({ field }) => (
              <TimeInput
                value={field.value}
                onChange={field.onChange}
                disabled={!isOpen || allDay}
                timeFormat="PM"
              />
            )}
          />
        </div>
      </td>
      <td className="px-3 py-4 text-sm text-gray-500">
        <Controller
          name={`businessHours.${day.key}.allDay`}
          control={control}
          defaultValue={allDay}
          render={({ field }) => (
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`allday-${day.key}`}
                checked={field.value}
                disabled={!isOpen}
                onCheckedChange={(checked) => {
                  const value = !!checked;
                  field.onChange(value);
                  if (value) {
                    setValue(`businessHours.${day.key}.from`, START_OF_DAY, { shouldDirty: true });
                    setValue(`businessHours.${day.key}.to`, END_OF_DAY_UI, { shouldDirty: true });
                  }
                }}
              />
              <span>All Day</span>
            </div>
          )}
        />
      </td>
    </tr>
  );
});

DayRow.displayName = 'DayRow';

const BusinessHoursTable: React.FC = memo(() => {
  const { control, watch, setValue } = useLeadDeliveryFormContext();

  const setAllDay = () => {
    DAYS.forEach((day) => {
      setValue(`businessHours.${day.key}.open`, true, { shouldDirty: true });
      setValue(`businessHours.${day.key}.from`, START_OF_DAY, { shouldDirty: true });
      setValue(`businessHours.${day.key}.to`, END_OF_DAY_UI, { shouldDirty: true });
      setValue(`businessHours.${day.key}.allDay`, true, { shouldDirty: true });
    });
  };

  // Watch the business hours object for each day's open status
  const businessHours = watch("businessHours") || {};

  return (
    <div className="overflow-x-auto">
      <div className="flex justify-end pb-2">
        <Button type="button" variant="secondary" size="sm" onClick={setAllDay}>
          Set 24/7
        </Button>
      </div>
      <table className="w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="py-3 pl-4 pr-3 text-left text-xs font-medium text-gray-500 sm:pl-6">
              Day
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">
              Status
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">
              Hours
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">
              All Day
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {DAYS.map((day) => (
            <DayRow
              key={day.key}
              day={day}
              control={control}
              isOpen={businessHours[day.key]?.open || false}
              defaultFrom={businessHours[day.key]?.from || '9:00 AM'}
              defaultTo={businessHours[day.key]?.to || '5:00 PM'}
              allDay={businessHours[day.key]?.allDay || false}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
});

BusinessHoursTable.displayName = 'BusinessHoursTable';

export default BusinessHoursTable;
